/**
 * Read-only hook exposing NCCN guideline review progress for an order.
 *
 * Fetches the latest stored evaluation (if any) and derives the fraction of the
 * mandatory (AI-Met and AI-Undetermined) guidelines that are fully reviewed;
 * Not-Met guidelines are optional (reviewed only via the override flow) and
 * excluded. A fresh order (no stored evaluation yet) resolves to `null`, which
 * callers use to distinguish "not started" from an in-progress review.
 *
 * The read is scoped by `order_id` and the caller's `org_id` (for RLS-scoped
 * reads), mirroring the Medical
 * Necessity screen's `getNccnEvaluation` call so both surfaces resolve to the
 * same stored version — the human-reviewed one — rather than a divergent
 * agent-populated row.
 *
 * Envelope columns are folded into {@link ReviewProgress}: `isComplete`
 * prefers `review_submitted`, and `orderResult` uses GET
 * `order_evaluation_result`. The progress ring still uses JSON `hasStarted` /
 * fraction so the arrow does not stay up until the summary gate.
 * @module useNccnReviewProgress
 */
import { useEffect, useState } from "react";
import { getNccnEvaluation } from "../../../api/postCall/nccnEvaluation";
import { LoadedNccnEvaluation } from "../../../data-model/nccnEvaluation";
import { EvaluationResult } from "../../../data-model/nccnGuidelines";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { fromGetResponse } from "../utils/loadEvaluationPayload";
import {
  computeMandatoryReviewProgress,
  ReviewProgress,
} from "../utils/nccnReviewFeedback";

/** Arguments for {@link useNccnReviewProgress}. */
export interface UseNccnReviewProgressArgs {
  /** Order to read the stored evaluation for. */
  orderId?: string;
}

/**
 * Result of {@link useNccnReviewProgress}: the computed progress plus an
 * explicit loading flag so callers can avoid rendering a misleading
 * intermediate state (e.g. the "not started" arrow) before the fetch resolves.
 */
export interface NccnReviewProgressResult {
  /**
   * The {@link ReviewProgress} when a stored evaluation exists, or `null` once
   * resolved with no stored evaluation yet (fresh order). Value is `null` while
   * loading too, so gate on {@link NccnReviewProgressResult.isLoading} first.
   */
  progress: ReviewProgress | null;
  /** True while the stored evaluation is being fetched. */
  isLoading: boolean;
}

/**
 * Overlays GET envelope flags onto JSON-derived mandatory progress.
 *
 * `hasStarted` and fraction stay JSON-derived. Completeness prefers the
 * envelope `review_submitted`. The final Met / Not Met verdict comes from
 * `order_evaluation_result` — the current order-level result, which is the
 * human-recomputed one once a review changed the AI's conclusion.
 *
 * @param loaded - Envelope plus payload from GET
 * @returns Progress for the regimen-bar Medical Necessity action
 */
const toOrderPageProgress = (loaded: LoadedNccnEvaluation): ReviewProgress => {
  const json = computeMandatoryReviewProgress(loaded.payload);
  return {
    ...json,
    isComplete: loaded.review_submitted || json.isComplete,
    orderResult:
      (loaded.order_evaluation_result as EvaluationResult | null) ?? null,
  };
};

/**
 * Loads and computes the guideline review progress for an order.
 *
 * @param args - Order identifier
 * @returns The {@link NccnReviewProgressResult} with the computed progress and
 *   an explicit loading flag
 */
export const useNccnReviewProgress = ({
  orderId,
}: UseNccnReviewProgressArgs): NccnReviewProgressResult => {
  const [progress, setProgress] = useState<ReviewProgress | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(orderId));

  useEffect(() => {
    if (!orderId) {
      setProgress(null);
      setIsLoading(false);
      return undefined;
    }

    let isActive = true;
    setIsLoading(true);
    setProgress(null);

    getNccnEvaluation({
      order_id: orderId,
      org_id: getOrgIdForFetchExternalWorklist() || null,
    })
      .then((response) => {
        if (!isActive) return;
        const loaded = fromGetResponse(response);
        setProgress(loaded ? toOrderPageProgress(loaded) : null);
        setIsLoading(false);
      })
      .catch((error) => {
        logError(error as Error, "Error loading NCCN review progress");
        if (isActive) {
          setProgress(null);
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [orderId]);

  return {
    progress,
    isLoading,
  };
};
